/**
 * Фото вейвера в отчёте оператора (вынесено из reportPlayers.tsx 18.09): ужатие до 2000px,
 * поворот, тумбы — ожидающие/неудачные (WvThumb) и уже загруженные (WvRemote, ⟳/✕ автору и
 * офису), сама секция WaiverAttach. Снимок уходит на сервер сразу при выборе (12.09).
 */
import { useEffect, useRef, useState } from "react";
import { api, apiFileUrl } from "../../api/client";
import { MediaViewer } from "./reportPlayers";
import m from "./opReport.module.css";

/** Поворот фото на 90° по часовой: canvas → новый File (в отчёт уйдёт повёрнутым). */
/**
 * Ужать снимок перед отправкой (12.09). Фото вейвера с телефона — это 3-6 МБ, а на
 * мобильной связи такая отправка рвётся: «фото не загрузилось» у Влада было именно про
 * это. Для читаемости бланка хватает 2000px по длинной стороне.
 */
async function shrink(f: File): Promise<File> {
  try {
    if (!f.type.startsWith("image/")) return f;
    const bmp = await createImageBitmap(f);
    const max = Math.max(bmp.width, bmp.height);
    if (max <= 2000 && f.size <= 1_500_000) return f;
    const k = max > 2000 ? 2000 / max : 1;
    const cv = document.createElement("canvas");
    cv.width = Math.round(bmp.width * k); cv.height = Math.round(bmp.height * k);
    const ctx = cv.getContext("2d"); if (!ctx) return f;
    ctx.drawImage(bmp, 0, 0, cv.width, cv.height);
    const blob = await new Promise<Blob | null>((res) => cv.toBlob(res, "image/jpeg", 0.85));
    return blob && blob.size < f.size
      ? new File([blob], f.name.replace(/\.\w+$/i, "") + ".jpg", { type: "image/jpeg" })
      : f;
  } catch { return f; }
}

/* 18.09 (ревью): при сбое НЕ возвращаем исходник — иначе ⟳ молча перезаливал тот же файл под новым
   ключом с записью «отредактирован» в аудите. EXIF-ориентацию учитываем, как <img>. */
async function rotate90(f: File): Promise<File> {
  const bmp = await createImageBitmap(f, { imageOrientation: "from-image" });
  const cv = document.createElement("canvas"); cv.width = bmp.height; cv.height = bmp.width;
  const ctx = cv.getContext("2d"); if (!ctx) throw new Error("Не удалось повернуть фото");
  ctx.translate(cv.width / 2, cv.height / 2); ctx.rotate(Math.PI / 2); ctx.drawImage(bmp, -bmp.width / 2, -bmp.height / 2);
  const blob = await new Promise<Blob | null>((res) => cv.toBlob(res, "image/jpeg", 0.92));
  if (!blob) throw new Error("Не удалось повернуть фото — слишком большой снимок");
  return new File([blob], f.name.replace(/\.\w+$/i, "") + ".jpg", { type: "image/jpeg" });
}

/* тумба вейвера: превью + ✕ и ⟳ (поворот на 90°, сколько угодно раз) прямо в миниатюре */
function WvThumb({ f, onOpen, onRemove, onRotate }: { f: File; onOpen: () => void; onRemove: () => void; onRotate: () => void }) {
  const [url] = useState(() => URL.createObjectURL(f));
  useEffect(() => () => URL.revokeObjectURL(url), [url]);
  return (
    <div className={m.th} title={f.name} onClick={onOpen} role="button" style={{ cursor: "pointer" }}>
      <img className={m.prev} src={url} alt="" />
      <button type="button" className={m.thx} onClick={(e) => { e.stopPropagation(); onRemove(); }} title="Убрать файл">✕</button>
      <button type="button" className={m.thr} onClick={(e) => { e.stopPropagation(); onRotate(); }} title="Повернуть на 90°">⟳</button>
    </div>
  );
}

/* фото вейверов в Game review: тумбы + добавить + просмотр (внутренний документ).
   26.08 (владелец): если коллега уже прикрепил — «Уже прикрепил …», прикрепление
   становится необязательным, но возможность остаётся. Поворот сохраняется в файле. */
/* 06.09 (жалоба Вани: «после возврата фото не прикреплено»): уже загруженные фото
   вейвера показываем тумбами по авторизованной ссылке; тап — просмотр. */
/* 18.09 (Аделина: «нет кнопки перевернуть, нельзя удалить фото, если прикрепил не то»):
   с 12.09 снимок уходит сразу, поэтому ⟳ и ✕ живут на загруженной тумбе. ⟳ — поворот
   с заменой файла на сервере (PUT), ✕ — двойной тап (удаление необратимо). Только автору и офису. */
function WvRemote({ id, name, editable, onChanged, onError }: { id: number; name: string; editable?: boolean; onChanged?: () => void; onError?: (msg: string) => void }) {
  const [url, setUrl] = useState<string | null>(null);
  const urlRef = useRef<string | null>(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [arm, setArm] = useState(false);
  const [gone, setGone] = useState(false);   // удалено на сервере — тумбу убираем сразу, не дожидаясь перезагрузки списка
  const swap = (next: string | null) => { if (urlRef.current) URL.revokeObjectURL(urlRef.current); urlRef.current = next; setUrl(next); };
  useEffect(() => {
    let alive = true;
    apiFileUrl(`/api/game-reports/waiver-file/${id}`).then((x) => { if (alive) swap(x); else URL.revokeObjectURL(x); }).catch(() => {});
    return () => { alive = false; swap(null); };
  }, [id]);
  useEffect(() => { if (!arm) return; const t = window.setTimeout(() => setArm(false), 3000); return () => window.clearTimeout(t); }, [arm]);
  const fail = (e: unknown, fallback: string) => onError?.(e instanceof Error && e.message ? e.message : fallback);
  const rotate = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!url || busy) return;
    setBusy(true);
    try {
      const blob = await fetch(url).then((r) => r.blob());
      const f = await rotate90(new File([blob], name || "waiver.jpg", { type: blob.type || "image/jpeg" }));
      const fd = new FormData(); fd.append("file", f);
      await api(`/api/game-reports/waiver-file/${id}`, { method: "PUT", body: fd, retries: 0 });
      swap(await apiFileUrl(`/api/game-reports/waiver-file/${id}`));
    } catch (err) { fail(err, "Не удалось повернуть фото"); } finally { setBusy(false); }
  };
  const remove = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (busy) return;
    if (!arm) { setArm(true); return; }
    setArm(false); setBusy(true);
    try {
      await api(`/api/game-reports/waiver-file/${id}`, { method: "DELETE", retries: 0 });
      swap(null); setGone(true);   // файл на сервере уже удалён; если перезагрузка списка упадёт — тумба всё равно исчезнет
      onChanged?.();
    } catch (err) { fail(err, "Не удалось удалить фото"); setBusy(false); }
  };
  if (gone) return null;
  return (<>
    <div className={m.th} title={name} onClick={() => url && setOpen(true)} role="button" style={{ cursor: "pointer", opacity: busy ? 0.55 : 1 }}>
      {url ? <img className={m.prev} src={url} alt="" /> : <span className={m.dur}>…</span>}
      {editable ? (<>
        <button type="button" className={m.thx} onClick={remove} disabled={busy}
          title={arm ? "Ещё раз — удалить" : "Удалить фото"} aria-label={arm ? "Ещё раз — удалить" : "Удалить фото"}
          style={arm ? { background: "var(--red)", color: "var(--black)", width: 34 } : undefined}>{arm ? "✕?" : "✕"}</button>
        <button type="button" className={m.thr} onClick={rotate} disabled={busy} title="Повернуть на 90°" aria-label="Повернуть на 90°">⟳</button>
        <span className={m.ok2} style={{ top: "auto", bottom: 2 }}>✓</span>
      </>) : <span className={m.ok2}>✓</span>}
    </div>
    {open && url ? <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: "calc(var(--z-modal) + 70)", background: "var(--black-a60)", display: "flex", alignItems: "center", justifyContent: "center", padding: 14 }}><img src={url} style={{ maxWidth: "100%", maxHeight: "88dvh", objectFit: "contain" }} alt="" /></div> : null}
  </>);
}

/**
 * Фото вейвера (12.09, владелец: «отправляю Владу пересдать отчёт, а у него бесконечные
 * проблемы»). Снимок теперь уходит СРАЗУ при выборе, а не вместе с отчётом.
 *
 * Так было нельзя: отчёт принимался, фото не долетало, человек читал «приложи снова» —
 * и упирался в «отчёт уже отправлен, правки после возврата из ОО». Замкнутый круг.
 * Загрузка вейвера — отдельный путь, она не зависит от сдачи отчёта, поэтому доложить
 * снимок можно в любой момент: и до отправки, и после возврата на исправление.
 */
export function WaiverAttach(
  { bookingId, attachedBy, existing = [], onUploaded, onBusy }:
  { bookingId: number; attachedBy?: string | null;
    existing?: { id: number; name: string; editable?: boolean }[]; onUploaded?: () => unknown;
    /** 01.10: сколько фото ещё в пути — «Отправить» ждёт загрузку (waiverBlock.tsx) */
    onBusy?: (n: number) => void },
) {
  const [view, setView] = useState<File | null>(null);
  const [busy, setBusy] = useState(0);
  const [failed, setFailed] = useState<File[]>([]);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { onBusy?.(busy); }, [busy, onBusy]);

  const send = async (list: File[]) => {
    setErr(null);
    setBusy((n) => n + list.length + 1);   // +1 — пока список фото не перечитан (иначе «фото нет» на миг)
    const bad: File[] = [];
    for (const raw of list) {
      /* 01.10: зависшая на мобильной связи загрузка не держит «Отправить» вечно — 60 с и в «не загрузилось» */
      const ac = new AbortController(); const tm = setTimeout(() => ac.abort(), 60_000);
      try {
        const f = await shrink(raw);
        const fd = new FormData();
        fd.append("file", f);
        await api(`/api/game-reports/${bookingId}/waiver`, { method: "POST", body: fd, retries: 1, signal: ac.signal });
      } catch (e: unknown) {
        bad.push(raw);
        setErr(ac.signal.aborted ? "Фото не загрузилось за минуту — проверь связь" : e instanceof Error ? e.message : "Не удалось загрузить фото");
      } finally {
        clearTimeout(tm);
        setBusy((n) => Math.max(0, n - 1));
      }
    }
    setFailed((prev) => [...prev, ...bad]);
    try { await onUploaded?.(); } finally { setBusy((n) => Math.max(0, n - 1)); }
  };

  const pick = (picked: File[]) => { if (picked.length) void send(picked); };

  return (<>
    <div className={m.lbl}>Фото вейвера{attachedBy ? <span className={m.wvBy}> · уже прикрепил {attachedBy}</span> : null}</div>
    <div className={m.media}>
      {existing.map((f) => <WvRemote key={`r${f.id}`} id={f.id} name={f.name} editable={f.editable} onChanged={onUploaded} onError={setErr} />)}
      {failed.map((f, i) => (
        <WvThumb key={`f${i}-${f.size}-${f.name}`} f={f} onOpen={() => setView(f)}
          onRemove={() => setFailed((p) => p.filter((_, j) => j !== i))}
          onRotate={() => { setFailed((p) => p.filter((_, j) => j !== i)); void send([f]); }} />
      ))}
      <label className={`${m.th} ${m.addm}`} title="Прикрепить фото вейвера">+
        <input type="file" accept="image/*" multiple style={{ display: "none" }}
          onChange={(e) => { pick(Array.from(e.target.files ?? [])); e.target.value = ""; }} />
      </label>
    </div>
    {busy ? <div className={m.wvNote}>Загружаю фото… {busy}</div> : null}
    {failed.length ? (
      <div className={m.wvErr}>
        Не загрузилось: {failed.length}. {err ? `${err}. ` : ""}Тап по ⟳ на снимке — попробовать снова.
      </div>
    ) : err ? <div className={m.wvErr}>{err}</div> : null}
    <MediaViewer file={view} onClose={() => setView(null)} />
  </>);
}
